import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_URL } from '../App';
import axios from 'axios';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import MessageViewer from '../components/MessageViewer';
import QRCode from 'qrcode';
import { trackEvent, GA_CATEGORIES } from '../utils/analytics';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

function Inbox() {
  const { email } = useParams();
  const navigate = useNavigate();
  const [inbox, setInbox] = useState(null);
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showShareModal, setShowShareModal] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [qrLoading, setQrLoading] = useState(false);
  const socketRef = useRef(null);
  const intervalRef = useRef(null);

  const loadInbox = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/email/${encodeURIComponent(email)}`);
      setInbox(response.data);
    } catch (error) {
      console.error('Error loading inbox:', error);
      if (error.response?.status === 404) {
        toast.error('Inbox not found or expired');
        navigate('/');
      }
    }
  }, [email, navigate]);

  const loadMessages = useCallback(async (showToast = false) => {
    try {
      if (showToast) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const response = await axios.get(`${API_URL}/email/${encodeURIComponent(email)}/messages`);
      setMessages(response.data.messages || []);
      if (showToast) {
        toast.success('Messages refreshed');
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      if (showToast) {
        toast.error('Failed to refresh messages');
      } else {
        toast.error('Failed to load messages');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [email]);

  useEffect(() => {
    if (!email) {
      navigate('/');
      return;
    }

    // GA4: Track inbox open
    trackEvent({
      category: GA_CATEGORIES.INBOX,
      action: 'Open Inbox',
      label: 'Inbox Page',
    });

    // Connect to Socket.IO
    socketRef.current = io(SOCKET_URL);
    socketRef.current.emit('subscribe', email);

    // Listen for new messages
    socketRef.current.on('new_message', (data) => {
      setMessages((prev) => [data.message, ...prev]);
      toast.success('New email received!', {
        icon: '📧',
      });
    });

    // Listen for inbox expiry
    socketRef.current.on('inbox_expired', () => {
      toast.error('Your inbox has expired');
      navigate('/');
    });

    // Load inbox and messages
    loadInbox();
    loadMessages();

    return () => {
      if (socketRef.current) {
        socketRef.current.emit('unsubscribe', email);
        socketRef.current.disconnect();
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [email, navigate, loadInbox, loadMessages]);

  // Timer effect - updates every second when inbox is loaded
  useEffect(() => {
    if (!inbox?.expiresAt) {
      return;
    }

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Update immediately
    const updateTimer = () => {
      const remaining = Math.max(0, Math.floor((new Date(inbox.expiresAt) - new Date()) / 1000));
      setTimeRemaining(remaining);
      if (remaining === 0) {
        toast.error('Inbox expired');
        navigate('/');
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      }
    };

    // Update immediately
    updateTimer();

    // Then update every second
    intervalRef.current = setInterval(updateTimer, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [inbox, navigate]);

  const handleRefresh = () => {
    loadMessages(true);
    loadInbox();
  };

  const copyEmail = () => {
    navigator.clipboard.writeText(email);
    toast.success('Email copied to clipboard!');

    // GA4: Track copy email
    trackEvent({
      category: GA_CATEGORIES.INTERACTION,
      action: 'Copy Email',
      label: 'Inbox',
    });
  };

  const copyEmailLink = () => {
    const url = `${window.location.origin}/inbox/${encodeURIComponent(email)}`;
    navigator.clipboard.writeText(url);
    toast.success('Inbox link copied!');
  };

  const openMailApp = () => {
    window.location.href = `mailto:${email}`;
  };

  const openShareModal = () => {
    setShowShareModal(true);
  };

  useEffect(() => {
    let cancelled = false;
    const makeQr = async () => {
      if (!showShareModal || !email) return;
      try {
        setQrLoading(true);
        // Mail apps + scanners understand mailto QR well
        const qrValue = `mailto:${email}`;
        const dataUrl = await QRCode.toDataURL(qrValue, {
          margin: 2,
          width: 320,
          color: {
            dark: '#111827', // gray-900
            light: '#FFFFFF',
          },
        });
        if (!cancelled) setQrDataUrl(dataUrl);
      } catch (e) {
        console.error('Failed to generate QR code:', e);
        toast.error('Failed to generate QR code');
      } finally {
        if (!cancelled) setQrLoading(false);
      }
    };
    makeQr();
    return () => {
      cancelled = true;
    };
  }, [email, showShareModal]);

  const deleteInbox = async () => {
    if (window.confirm('Are you sure you want to delete this inbox?')) {
      try {
        await axios.delete(`${API_URL}/email/${encodeURIComponent(email)}`);
        toast.success('Inbox deleted');
        navigate('/');
      } catch (error) {
        console.error('Error deleting inbox:', error);
        toast.error('Failed to delete inbox');
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const unreadCount = messages.filter((m) => !m.read).length;

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6 fade-in">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1">
              <button
                onClick={() => navigate('/')}
                className="text-blue-600 hover:text-blue-700 mb-2 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Home
              </button>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Your Inbox</h1>
              <div className="flex items-center gap-3 flex-wrap">
                <p className="font-mono text-blue-600 break-all">{email}</p>
                <button
                  onClick={copyEmail}
                  className="text-sm bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded transition-colors"
                >
                  Copy
                </button>
                <button
                  onClick={openShareModal}
                  className="text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 px-3 py-1 rounded transition-colors flex items-center gap-2"
                  title="Share via QR code"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5h4v4H5V5zm10 0h4v4h-4V5zM5 15h4v4H5v-4zm6 0h2v2h-2v-2zm4 0h4v4h-4v-4zM11 5h2v2h-2V5zm0 4h2v2h-2V9zm4 0h2v2h-2V9z" />
                  </svg>
                  Share
                </button>
                <button
                  onClick={openMailApp}
                  className="text-sm bg-emerald-50 text-emerald-700 hover:bg-emerald-100 px-3 py-1 rounded transition-colors flex items-center gap-2"
                  title="Open in your mail app"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8m-16 11h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Open
                </button>
                {inbox && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded font-medium">
                      {inbox.messageCount || 0} {inbox.messageCount === 1 ? 'email' : 'emails'}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-sm text-gray-600">Time Remaining</div>
                <div className={`text-xl font-bold ${timeRemaining < 300 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatTime(timeRemaining)}
                </div>
              </div>
              <button
                onClick={deleteInbox}
                className="group relative bg-gradient-to-r from-red-500 via-red-600 to-red-700 text-white px-8 py-3 rounded-xl font-semibold hover:from-red-600 hover:via-red-700 hover:to-red-800 transition-all duration-300 shadow-lg hover:shadow-2xl hover:shadow-red-500/50 flex items-center gap-3 overflow-hidden transform hover:scale-105 active:scale-95"
              >
                {/* Animated background effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                
                {/* Icon with animation */}
                <div className="relative z-10 flex items-center justify-center">
                  <svg className="w-6 h-6 group-hover:rotate-12 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                
                {/* Text */}
                <span className="relative z-10 text-base tracking-wide">Delete Inbox</span>
                
                {/* Warning indicator */}
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-pulse group-hover:animate-none group-hover:bg-yellow-300"></div>
              </button>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Messages List */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-lg shadow-md overflow-hidden fade-in">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-semibold text-lg">Messages</h2>
                    {inbox && (
                      <p className="text-xs text-white/80 mt-1">
                        {inbox.messageCount || messages.length || 0} total • {unreadCount} unread
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {unreadCount > 0 && (
                      <span className="bg-white text-blue-600 text-xs font-bold px-2 py-1 rounded-full">
                        {unreadCount} new
                      </span>
                    )}
                    <button
                      onClick={handleRefresh}
                      disabled={refreshing || loading}
                      className="bg-white/20 hover:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed text-white p-2 rounded-lg transition-colors flex items-center gap-1"
                      title="Refresh messages"
                    >
                      <svg 
                        className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              <div className="max-h-[600px] overflow-y-auto">
                {loading ? (
                  <div className="p-8 text-center">
                    <div className="spinner mx-auto"></div>
                    <p className="text-gray-500 mt-4">Loading messages...</p>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <div className="text-4xl mb-4">📭</div>
                    <p>No messages yet</p>
                    <p className="text-sm mt-2">Emails sent to this address will appear here</p>
                  </div>
                ) : (
                  messages.map((message) => (
                    <div
                      key={message._id}
                      onClick={() => setSelectedMessage(message)}
                      className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                        !message.read ? 'bg-blue-50' : ''
                      } ${selectedMessage?._id === message._id ? 'bg-blue-100' : ''}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-800 truncate">
                            {message.fromName || message.from}
                          </p>
                          <p className="text-sm text-gray-500 truncate">{message.from}</p>
                        </div>
                        {!message.read && (
                          <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full ml-2">
                            New
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-medium text-gray-700 truncate mb-1">
                        {message.subject}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatDistanceToNow(new Date(message.receivedAt), { addSuffix: true })}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Message Viewer */}
          <div className="md:col-span-2">
            {selectedMessage ? (
              <MessageViewer
                message={selectedMessage}
                email={email}
                onClose={() => setSelectedMessage(null)}
              />
            ) : (
              <div className="bg-white rounded-lg shadow-md p-12 text-center fade-in">
                <div className="text-6xl mb-4">📧</div>
                <p className="text-gray-500 text-lg">
                  Select a message to view its contents
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Share Modal (QR) */}
      {showShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowShareModal(false)}
          />
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold">Share Email</h3>
                  <p className="text-white/80 text-sm mt-1">Scan the QR to open a mail app</p>
                </div>
                <button
                  onClick={() => setShowShareModal(false)}
                  className="text-white/90 hover:text-white"
                  aria-label="Close"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex flex-col items-center">
                {qrLoading ? (
                  <div className="flex flex-col items-center py-10">
                    <div className="spinner"></div>
                    <p className="text-gray-600 mt-3 text-sm">Generating QR...</p>
                  </div>
                ) : (
                  qrDataUrl && (
                    <img
                      src={qrDataUrl}
                      alt="QR code for email"
                      className="w-64 h-64 rounded-lg bg-white"
                    />
                  )
                )}

                <div className="mt-4 w-full">
                  <div className="text-xs text-gray-500 mb-1">Email</div>
                  <div className="font-mono text-sm text-gray-900 break-all bg-white border border-gray-200 rounded-lg p-3">
                    {email}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
                <button
                  onClick={copyEmail}
                  className="bg-gray-900 text-white rounded-xl px-4 py-3 font-semibold hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-4 12h6a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy Email
                </button>
                <button
                  onClick={copyEmailLink}
                  className="bg-blue-600 text-white rounded-xl px-4 py-3 font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 010 5.656l-1.414 1.414a4 4 0 01-5.656 0 4 4 0 010-5.656l1.414-1.414m6.364-6.364a4 4 0 015.656 0 4 4 0 010 5.656l-1.414 1.414a4 4 0 01-5.656 0" />
                  </svg>
                  Copy Link
                </button>
                <button
                  onClick={openMailApp}
                  className="bg-emerald-600 text-white rounded-xl px-4 py-3 font-semibold hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 sm:col-span-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8m-16 11h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Open Mail App
                </button>
              </div>

              <p className="text-xs text-gray-500 mt-4">
                Tip: On mobile, scanning the QR usually opens your default mail app with this address filled in.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Inbox;

